/**
 * Jertscript2 Lexer
 * Tokenizes Jertscript2 source code into a stream of tokens
 */

import { Token, TokenType } from './tokens';
import { KEYWORDS, getKeywordType } from './keywords';

// Re-export everything from tokens and keywords
export { Token, TokenType } from './tokens';
export { KEYWORDS, isKeyword, getKeywordType } from './keywords';

/**
 * Lexer error thrown when invalid syntax is encountered
 */
export class LexerError extends Error {
  constructor(
    message: string,
    public readonly line: number,
    public readonly column: number
  ) {
    super(`[Ligne ${line}, Colonne ${column}] Erreur lexicale: ${message}`);
    this.name = 'LexerError';
  }
}

/**
 * Lexer class for tokenizing Jertscript2 source code
 */
export class Lexer {
  private readonly source: string;
  private readonly tokens: Token[] = [];

  private start: number = 0;
  private current: number = 0;
  private line: number = 1;
  private column: number = 1;
  private startColumn: number = 1;

  /**
   * Create a new Lexer instance
   * @param source The Jertscript2 source code to tokenize
   */
  constructor(source: string) {
    this.source = source;
  }

  /**
   * Tokenize the source code
   * @returns Array of tokens
   */
  tokenize(): Token[] {
    while (!this.isAtEnd()) {
      this.start = this.current;
      this.startColumn = this.column;
      this.scanToken();
    }

    // Add EOF token
    this.tokens.push({
      type: TokenType.EOF,
      lexeme: '',
      line: this.line,
      column: this.column,
    });

    return this.tokens;
  }

  /**
   * Scan the next token from the source
   */
  private scanToken(): void {
    const char = this.advance();

    switch (char) {
      // Single character tokens
      case '(': this.addToken(TokenType.LPAREN); break;
      case ')': this.addToken(TokenType.RPAREN); break;
      case '{': this.addToken(TokenType.LBRACE); break;
      case '}': this.addToken(TokenType.RBRACE); break;
      case '[': this.addToken(TokenType.LBRACKET); break;
      case ']': this.addToken(TokenType.RBRACKET); break;
      case ',': this.addToken(TokenType.COMMA); break;
      case '.': this.addToken(TokenType.DOT); break;
      case ':': this.addToken(TokenType.COLON); break;
      case ';': this.addToken(TokenType.SEMICOLON); break;
      case '?': this.addToken(TokenType.QUESTION); break;
      case '%': this.addToken(TokenType.PERCENT); break;
      case '*': this.addToken(TokenType.STAR); break;

      // Potentially compound operators
      case '+':
        this.addToken(this.match('=') ? TokenType.PLUS_EQUALS : TokenType.PLUS);
        break;
      case '-':
        this.addToken(this.match('=') ? TokenType.MINUS_EQUALS : TokenType.MINUS);
        break;
      case '=':
        if (this.match('=')) {
          this.addToken(this.match('=') ? TokenType.EST : TokenType.DOUBLE_EQUALS);
        } else if (this.match('>')) {
          this.addToken(TokenType.ARROW);
        } else {
          this.addToken(TokenType.EQUALS);
        }
        break;
      case '!':
        if (this.match('=')) {
          this.addToken(this.match('=') ? TokenType.NEST_PAS : TokenType.NOT_EQUALS);
        } else {
          this.addToken(TokenType.BANG);
        }
        break;
      case '<':
        this.addToken(this.match('=') ? TokenType.LESS_EQUALS : TokenType.LESS);
        break;
      case '>':
        this.addToken(this.match('=') ? TokenType.GREATER_EQUALS : TokenType.GREATER);
        break;
      case '&':
        if (this.match('&')) {
          this.addToken(TokenType.ET);
        } else {
          this.addToken(TokenType.AMPERSAND);
        }
        break;
      case '|':
        if (this.match('|')) {
          this.addToken(TokenType.OU);
        } else {
          this.addToken(TokenType.PIPE);
        }
        break;

      // Slash can be division or start of comment
      case '/':
        if (this.match('/')) {
          // Single-line comment - consume until end of line
          while (this.peek() !== '\n' && !this.isAtEnd()) {
            this.advance();
          }
        } else {
          this.addToken(TokenType.SLASH);
        }
        break;

      // Whitespace
      case ' ':
      case '\r':
      case '\t':
        // Ignore whitespace
        break;

      case '\n':
        this.addToken(TokenType.NEWLINE);
        this.line++;
        this.column = 1;
        break;

      // String literals
      case '"':
        this.string();
        break;

      default:
        if (this.isDigit(char)) {
          this.number();
        } else if (this.isAlpha(char)) {
          this.identifier();
        } else {
          throw new LexerError(`Caractere inattendu '${char}'`, this.line, this.startColumn);
        }
    }
  }

  /**
   * Scan a string literal
   */
  private string(): void {
    const startLine = this.line;
    const startCol = this.startColumn;

    while (this.peek() !== '"' && !this.isAtEnd()) {
      if (this.peek() === '\n') {
        this.line++;
        this.column = 1;
      }
      // Handle escape sequences
      if (this.peek() === '\\' && this.peekNext() !== '\0') {
        this.advance(); // consume backslash
      }
      this.advance();
    }

    if (this.isAtEnd()) {
      throw new LexerError('Chaine non terminee', startLine, startCol);
    }

    // Consume the closing "
    this.advance();

    // Extract the string value (without quotes)
    const value = this.source.substring(this.start + 1, this.current - 1);
    // Process escape sequences
    const processed = this.processEscapes(value);
    this.addToken(TokenType.STRING, processed);
  }

  /**
   * Process escape sequences in a string
   */
  private processEscapes(str: string): string {
    let result = '';
    let i = 0;
    while (i < str.length) {
      if (str[i] === '\\' && i + 1 < str.length) {
        const next = str[i + 1];
        switch (next) {
          case 'n': result += '\n'; break;
          case 't': result += '\t'; break;
          case 'r': result += '\r'; break;
          case '\\': result += '\\'; break;
          case '"': result += '"'; break;
          default: result += str[i + 1]; break;
        }
        i += 2;
      } else {
        result += str[i];
        i++;
      }
    }
    return result;
  }

  /**
   * Scan a number literal (integer or decimal)
   */
  private number(): void {
    // Consume integer part
    while (this.isDigit(this.peek())) {
      this.advance();
    }

    // Look for decimal part
    if (this.peek() === '.' && this.isDigit(this.peekNext())) {
      // Consume the dot
      this.advance();

      // Consume decimal digits
      while (this.isDigit(this.peek())) {
        this.advance();
      }
    }

    const lexeme = this.source.substring(this.start, this.current);
    const value = parseFloat(lexeme);
    this.addToken(TokenType.NUMBER, value);
  }

  /**
   * Scan an identifier or keyword
   */
  private identifier(): void {
    while (this.isAlphaNumeric(this.peek())) {
      this.advance();
    }

    const text = this.source.substring(this.start, this.current);
    const type = getKeywordType(text);
    this.addToken(type);
  }

  /**
   * Check if we've reached the end of the source
   */
  private isAtEnd(): boolean {
    return this.current >= this.source.length;
  }

  /**
   * Advance to the next character and return the current one
   */
  private advance(): string {
    const char = this.source[this.current];
    this.current++;
    this.column++;
    return char;
  }

  /**
   * Look at the current character without consuming it
   */
  private peek(): string {
    if (this.isAtEnd()) return '\0';
    return this.source[this.current];
  }

  /**
   * Look at the next character without consuming it
   */
  private peekNext(): string {
    if (this.current + 1 >= this.source.length) return '\0';
    return this.source[this.current + 1];
  }

  /**
   * Consume the current character if it matches the expected one
   * @param expected The character to match
   * @returns true if the character matched and was consumed
   */
  private match(expected: string): boolean {
    if (this.isAtEnd()) return false;
    if (this.source[this.current] !== expected) return false;

    this.current++;
    this.column++;
    return true;
  }

  /**
   * Check if a character is a digit (0-9)
   */
  private isDigit(char: string): boolean {
    return char >= '0' && char <= '9';
  }

  /**
   * Check if a character is alphabetic or underscore
   */
  private isAlpha(char: string): boolean {
    return (char >= 'a' && char <= 'z') ||
           (char >= 'A' && char <= 'Z') ||
           char === '_';
  }

  /**
   * Check if a character is alphanumeric or underscore
   */
  private isAlphaNumeric(char: string): boolean {
    return this.isAlpha(char) || this.isDigit(char);
  }

  /**
   * Add a token to the tokens array
   */
  private addToken(type: TokenType, literal?: string | number | null): void {
    const lexeme = this.source.substring(this.start, this.current);
    this.tokens.push({
      type,
      lexeme,
      literal,
      line: this.line,
      column: this.startColumn,
    });
  }
}

/**
 * Convenience function to tokenize source code
 * @param source The Jertscript2 source code
 * @returns Array of tokens
 */
export function tokenize(source: string): Token[] {
  const lexer = new Lexer(source);
  return lexer.tokenize();
}
